import { Badge, Box, Chip, IconButton, Switch } from "@mui/material";
import { useCallback, useState } from "react";
import { IDataUsuarios } from "../../../types/api/configuracoes/pesquisar-usuarios";
import TableCellPrinter from "../../../components/TableCellPrinter";
import IRequest from "../../../types/IRequest";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DialogCriacaoEditUser from "./DialogCriacaoEditUser";
import { TParamExtras, TParamsObrigatorios } from "../../../types/api/configuracoes/detalhar-usuario";
import SessaoConfiguracao, { ConfiguracaoColunas } from "../../../components/BasicComponents/DisplayDados/SessaoConfiguracao";
import { confirm } from "material-ui-confirm";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { TAttUsuarioPerfilInput } from "../../../types/mutations/atualizar-usuario";

export default function Usuarios() {

  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState<TOpenModalUsuario>(null);

  //Querys e mutations
  const { mutate: mudarStatus, isLoading: isLoadingAttUsuario } = useMutation({
    mutationKey: "atualizar-usuario",
    mutationFn: (inputs: IAttUsuarioStatusInput) =>
      axiosClient.put(`/atualizar-usuario/${inputs.id}`, {
        ...inputs,
        "atualiza_pefil": "false"
      }),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar usuário!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries("pesquisar-usuarios");
      queryClient.invalidateQueries(`detalhar-usuario/{id}`);
      enqueueSnackbar("Atualização do usuário realizada com sucesso!", {
        variant: "success",
      });
    },
  });

  //funções manipuladoras
  const handleModalAction = useCallback((action: "open" | "close", id?: true | number, value?: IDataUsuarios | null) => {
    if (action === "close") setOpenModal(null)
    if (action === "open" && id && value) setOpenModal({ open: id, defaultValue: value })
    if (action === "open" && id && !value) setOpenModal({ open: id, defaultValue: null })
  }, [])

  const handleChangeStatus = useCallback(
    async (usuario: IAttUsuarioStatusInput) => {
      confirm({
        description: (
          <>
            Deseja realmente <b>{usuario.status === "Ativo" ? "ativar" : "desativar"}</b> o(a) usuário(a) <b>{usuario.nome}</b>?
          </>
        ),
        title: 'Confirmação',
        confirmationText: `Confirmar`,
        confirmationButtonProps: { variant: 'contained', color: 'success' },
      })
        .then(() => mudarStatus(usuario))
        .catch(() => { });
    },
    [mudarStatus]
  );
  //

  //Valores das colunas
  const colunas: ConfiguracaoColunas<IDataUsuarios>[] = [
    {
      campo: "nome",
      titulo: "Nome",
      render: (_, usuario) => (
        <TableCellPrinter label={usuario.nome} subLabel={usuario.email} />
      )
    },
    {
      campo: "perfis",
      titulo: "Perfil",
      render: (_, usuario) => {
        const primeiroPerfil = usuario.perfis[0].nome;
        const perfis = usuario.perfis;

        return (
          <Box display="flex" alignItems="center">
            <Chip
              icon={<ManageAccountsIcon />}
              label={primeiroPerfil}
              sx={{ mr: 1 }}
            />
            {perfis.length > 1 && (
              <IconButton aria-label="cart">
                <Badge badgeContent={`+${perfis.length - 1}`} color="success">
                  <ManageAccountsIcon color="action" />
                </Badge>
              </IconButton>
            )}
          </Box>
        );
      }
    },
    {
      campo: "status",
      titulo: "Status"
    }
  ]
  //

  return (
    <>
      <SessaoConfiguracao<IDataUsuarios>
        podeDeletar={() => false}
        titulo="Usuários"
        endpoints={{
          listar: "pesquisar-usuarios",
          deletar: "excluir-usuario",
        }}
        colunas={colunas}
        queryKey="pesquisar-usuarios"
        onEditar={(usuario) => {
          handleModalAction("open", usuario.id, usuario)
        }}
        onAdicionar={() => {
          handleModalAction("open", true, null)
        }}
        mensagemSucessoDeletar="Exclusão do usuário realizada com sucesso!"
        mensagemErroDeletar="Erro ao excluir usuário!"
        mensagemSemDados="Nenhum usuário encontrado."
        botoesPersonalizados={[
          {
            icon: <ManageAccountsIcon />,
            onClick: (usuario) => {
              handleChangeStatus({...usuario, status: usuario.status === "Ativo" ? "Inativo" : "Ativo", perfis: usuario.perfis.map(perfil => ({ perfil_id: perfil.id }))});
            },
            tooltip: "Gerenciar perfis"
          }
        ]}
      />

      {
        Boolean(openModal) &&
        <DialogCriacaoEditUser
          defaultValue={openModal?.defaultValue ? openModal : null}
          open={Boolean(openModal)}
          handleClose={() => handleModalAction("close")}
        />
      }
    </>
  );
}

export interface IUsuarios {
  requestUsuarios: IRequest<IDataUsuarios>;
  dataUsuarios: IDataUsuarios[];
}

export type TOpenModalUsuario = {
  open: number | boolean | null;
  defaultValue: IDataUsuarios | null;
} | null

export type TPerfilValue = {
  index: number | null;
  id: number;
  nome: string;
  parametros_obrigatorios: Array<TParamsObrigatorios>;
  loja: TParamExtras | null;
  vendedor: TParamExtras | null;
  condicoes_pagamento_blocked: TParamExtras[] | null;
  limite_alcada: number | null;
  tipo_publico: string[] | null;
};

interface IAttUsuarioStatusInput {
  id: number,
  nome: string,
  email: string,
  perfis: Array<TAttUsuarioPerfilInput>
  status: "Ativo" | "Inativo";
}

